import { useState, type ReactNode } from 'react';
import { List, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Card, ConfirmDialog, EmptyState, IconButton } from '@/components/ui';
import { DnsRecordDialog } from '@/components/sites/DnsRecordDialog';
import type { ZoneSectionProps } from '@/components/sites/dns/ZoneSections';
import { useRecordChange } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import type { DnsRecordView } from '@/types/api';

/** A domain's DNS records, each changed in 2d2s the moment it is confirmed. */
export function RecordsSection({ siteId, zone, may }: ZoneSectionProps): ReactNode {
  const [editing, setEditing] = useState<DnsRecordView | 'new' | null>(null);
  const [deleting, setDeleting] = useState<DnsRecordView | null>(null);
  const domainId = zone.domainId ?? '';
  const remove = useRecordChange(siteId, {
    onSuccess: () => {
      toast.success('Запись удалена');
      setDeleting(null);
    },
  });

  return (
    <Card
      flush
      title="DNS-записи"
      icon={List}
      actions={
        may ? (
          <Button size="sm" icon={Plus} onClick={() => setEditing('new')}>
            Добавить запись
          </Button>
        ) : null
      }
    >
      {zone.records.length === 0 ? (
        <EmptyState icon={List} message="Записей нет" />
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Тип</th>
                <th scope="col">Имя</th>
                <th scope="col">Значение</th>
                <th scope="col">TTL</th>
                <th scope="col">Прокси</th>
                {may ? <th scope="col" aria-label="Действия" /> : null}
              </tr>
            </thead>
            <tbody>
              {zone.records.map((record) => (
                <tr key={record.id}>
                  <td className="mono">{record.type}</td>
                  <td className="mono">{record.name}</td>
                  <td className="mono" style={{ wordBreak: 'break-all' }}>
                    {record.priority !== null ? <span className="muted">{record.priority} </span> : null}
                    {record.content}
                  </td>
                  <td>{record.ttl === 1 ? 'авто' : `${record.ttl} с`}</td>
                  <td>{record.proxied === null ? '—' : record.proxied ? 'да' : 'нет'}</td>
                  {may ? (
                    <td className="nowrap">
                      <IconButton icon={Pencil} size="sm" variant="ghost" label="Изменить" onClick={() => setEditing(record)} />
                      <IconButton icon={Trash2} size="sm" variant="ghost" label="Удалить" onClick={() => setDeleting(record)} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing ? <DnsRecordDialog siteId={siteId} zone={zone} record={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}

      <ConfirmDialog
        open={deleting !== null}
        title="Удалить DNS-запись"
        description={deleting ? `${deleting.type} ${deleting.name}` : undefined}
        confirmLabel="Удалить"
        destructive
        busy={remove.isPending}
        error={remove.isError ? errorText(remove.error) : undefined}
        onConfirm={() => deleting && remove.mutate({ kind: 'delete', domainId, recordId: deleting.id })}
        onClose={() => {
          remove.reset();
          setDeleting(null);
        }}
      >
        <p className="field-hint">
          Изменение применяется сразу. Без записи для {zone.names.join(', ')} сайт станет недоступен.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
